import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import SearchInput from "../../../utils/SearchInput";
import PaginationComponent from "../../../utils/PaginationComponent";
import TableLoader from "../../../utils/TableLoader";
import { FaPlus } from "react-icons/fa";
import { TbEdit } from "react-icons/tb";
import { BiSolidTrashAlt } from "react-icons/bi";
import { motion, AnimatePresence } from "framer-motion";
import {
  containerVariants,
  itemVariants,
  buttonAnimations,
  cardVariants
} from "../../../utils/Animations";
import UserEdit from "./UserEdit";
import DashboardTooltip from "../Components/DashboardTooltip";
import ConfirmationDialog from "../../../utils/ConfirmationDialog";
import { isMasterAdmin } from "../../../utils/adminSecurity";

const UserManagement = forwardRef((props, ref) => {
  const { hasPermission } = props;
  const navigate = useNavigate();

  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const token = localStorage.getItem("token");
  const loginUserId = localStorage.getItem("loginUserId");

  const [isLoading, setIsLoading] = useState({ main: false, edit: false });
  const [users, setUsers] = useState([]);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile_phone, setMobilePhone] = useState("");
  const [selectedState, setSelectedState] = useState("Active");
  const [selectedRole, setSelectedRole] = useState("");
  const [errors, setErrors] = useState({});

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const getAdminUser = async () => {
    setIsLoading({ ...isLoading, main: true });
    try {
      let params;
      if (searchText) {
        params = {
          pageNumber: page,
          size: rowsPerPage,
          search: searchText,
        };
      } else {
        params = {
          pageNumber: page,
          size: rowsPerPage,
        };
      }
      const response = await axios.get(`${apiUrl}/api/v1/users`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response?.data;
      setUsers(data?.data);
      setTotalPages(data?.total);
    } catch (error) {
      console.error('Error fetching order data:', error);
    } finally {
      setIsLoading({ ...isLoading, main: false });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!name || !name.trim()) {
      newErrors.name = "Name is required.";
    }
    if (!surname || !surname.trim()) {
      newErrors.surname = "Surname is required.";
    }
    if (!email || !email.trim()) {
      newErrors.email = "Email is required.";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        newErrors.email = "Please enter a valid email address.";
      }
    }
    if (!mobile_phone || !mobile_phone.trim()) {
      newErrors.mobile_phone = "Mobile phone is required.";
    }
    if (!selectedState) {
      newErrors.status = "Status is required.";
    }
    if (!selectedRole) {
      newErrors.role = "Role is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading({ ...isLoading, edit: true });
    try {
      const payload = {
        name,
        surname,
        email,
        password,
        mobile_phone,
        status: selectedState,
        role_id: selectedRole,
      };
      const response = await axios.put(
        `${apiUrl}/api/v1/users/${currentUserData?.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response?.status === 200 || response?.status === 201) {
        getAdminUser();
        toast.success(response?.data?.message || response?.message || 'User updated successfully!', {
          theme: 'colored',
          autoClose: 1000,
        });
        setIsEditing(false);
        setErrors({});
      }
    } catch (error) {
      console.error('Error Editing user:', error?.response?.data || error);
      const apiErrors = error?.response?.data?.errors;
      let firstErrorMessage = '';
      if (apiErrors && typeof apiErrors === 'object') {
        const errorValues = Object.values(apiErrors).flat();
        if (errorValues.length > 0 && typeof errorValues[0] === 'string') {
          firstErrorMessage = errorValues[0];
        }
      }
      if (!firstErrorMessage) {
        firstErrorMessage = error?.response?.data?.message || 'Something went wrong. Please try again.';
      }
      toast.error(firstErrorMessage, {
        theme: 'colored',
        autoClose: 3000,
      });
    } finally {
      setIsLoading({ ...isLoading, edit: false });
    }
  };

  const handleDelete = async (id, email) => {
    if (!id || (loginUserId && String(id) === String(loginUserId)) || isMasterAdmin(email)) {
      return;
    }
    setIsLoading({ ...isLoading, main: true });
    try {
      const response = await axios.delete(`${apiUrl}/api/v1/users/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response?.status === 200 || response?.status === 204) {
        toast.success(response?.data?.message || 'User deleted successfully', {
          theme: "colored",
          autoClose: 1000,
        });
        getAdminUser();
      }
    } catch (error) {
      console.error('Error deleting user:', error?.response?.data || error.message);
      toast.error(error?.response?.data?.message || 'Failed to delete the user. Please try again.', {
        theme: 'colored',
        autoClose: 3000,
      });
    } finally {
      setIsLoading({ ...isLoading, main: false });
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete?.id || isMasterAdmin(userToDelete?.email)) return;
    const id = userToDelete.id;
    const email = userToDelete.email;
    setDeleteModalOpen(false);
    setUserToDelete(null);
    await handleDelete(id, email);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const handleEditClick = (user) => {
    setIsEditing(true);
    setCurrentUserData(user);
    setErrors({});
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentUserData(null);
    setErrors({});
  };

  useImperativeHandle(ref, () => ({
    user() {
      setIsEditing(false);
      setCurrentUserData(null);
    },
  }));

  useEffect(() => {
    if (currentUserData) {
      setName(currentUserData?.name || "");
      setSurname(currentUserData?.surname || "");
      setEmail(currentUserData?.email || "");
      setPassword(currentUserData?.password || "");
      setMobilePhone(currentUserData?.mobile_phone || "");
      setSelectedState(currentUserData?.status || "Active");
      setSelectedRole(currentUserData?.role_id || "");
    }
  }, [currentUserData]);

  useEffect(() => {
    getAdminUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchText]);

  useEffect(() => {
    const handlePageRefresh = () => {
      localStorage.removeItem("searchTextUser");
    };
    window.addEventListener("beforeunload", handlePageRefresh);
    return () => {
      window.removeEventListener("beforeunload", handlePageRefresh);
    };
  }, []);

  return (
    <>
      <AnimatePresence mode="wait">
      {isEditing ? (
        <motion.div
          key="edit-category"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <UserEdit
            isLoading={isLoading}
            name={name}
            surname={surname}
            email={email}
            mobile_phone={mobile_phone}
            selectedState={selectedState}
            selectedRole={selectedRole}
            setName={setName}
            setSurname={setSurname}
            setEmail={setEmail}
            setMobilePhone={setMobilePhone}
            setSelectedState={setSelectedState}
            setSelectedRole={setSelectedRole}
            handleSave={handleSave}
            handleCancel={handleCancel}
            errors={errors}
            setErrors={setErrors}
            currentUserData={currentUserData}
          />
        </motion.div>
      ) : (
        <motion.div
          key="user-list"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          <motion.section
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="border-t border-[#D9D9D9] sm:mt-5 sm:pt-10 pt-5"
          >
            <motion.div
              className="flex lg:flex-row flex-col justify-between items-center mt-3 mb-5 w-full"
              variants={containerVariants}
            >
              <motion.h3
                className="text-3xl font-bold mb-3 xl:mb-0 poppins-medium self-start"
                variants={itemVariants}
              >
                Lista Utenti
              </motion.h3>
              <motion.div
                className="self-start flex items-center gap-4 sm:flex-row flex-col sm:w-auto w-full"
                variants={itemVariants}
              >
                <SearchInput
                  onSearchChange={handleSearchChange}
                  component="Utenti Admin"
                  placeholder="Utenti"
                />
                {hasPermission("Users", "add") && (
                  <motion.button
                    className="flex sm:w-auto w-full items-center justify-center px-2 py-1 bg-[#8A2533] text-white rounded-lg border-2 border-[#953543] shadow-sm hover:bg-[#8A2533] transition-all duration-200 group"
                    onClick={() => navigate("/admin/user-add")}
                    aria-label="Aggiungi nuovo utente"
                    {...buttonAnimations}
                  >
                    <FaPlus />
                    <span className="ml-2 poppins-regular">Aggiungi</span>
                  </motion.button>
                )}
              </motion.div>
            </motion.div>
            <motion.div
              className="user-mangement-main w-auto h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] px-6 pt-3 pb-0 mb-3 rounded-xl shadow"
              variants={cardVariants}
            >
              <table className="w-full text-left wine_table">
                <thead>
                  <motion.tr
                    className="border-b border-[#8B2433]"
                    variants={itemVariants}
                  >
                    <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Nome</th>
                    <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Cognome</th>
                    <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Cellulare</th>
                    <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Email</th>
                    <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Stato</th>
                    {(hasPermission("Users", "edit") || hasPermission("Users", "remove")) && (
                      <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Opzioni</th>
                    )}
                  </motion.tr>
                </thead>
                {isLoading?.main ? (
                  <TableLoader />
                ) : (
                  <tbody>
                    {users?.length ? (
                      users?.map((user, index) => (
                        <motion.tr
                          key={index}
                          className="border-b border-[#8B2433]/12"
                          variants={itemVariants}
                          custom={index}
                        >
                          <td className="py-4 px-4 text-left poppins-medium text-[#303030]">{user?.name?.replace(/\s+/g, ' ') || "N/A"}</td>
                          <td className="py-4 px-4 text-left poppins-medium text-[#303030]">{user?.surname?.replace(/\s+/g, ' ') || "N/A"}</td>
                          <td className="py-4 px-4 text-left poppins-medium text-[#303030]">{user?.mobile_phone?.trim() || "N/A"}</td>
                          <td className="py-4 px-4 text-left poppins-medium text-[#303030]">
                            <DashboardTooltip text={user?.email} />
                          </td>
                          <td className="py-4 px-2 text-left">
                            <span
                              className={`inline-flex items-center justify-center w-20 py-1 px-3 rounded-full text-center font-extrabold transition-colors ${user?.status === "Active"
                                ? "text-[#0AC83D] active_lable"
                                : "text-[#EB2D2D] inactive_lable"
                                }`}
                            >
                              {user?.status === "Active" ? "Attivo" : user?.status === "Inactive" ? "Inattivo" : "N/A"}
                            </span>
                          </td>
                          {(hasPermission("Users", "edit") || hasPermission("Users", "remove")) && (
                            <td className="py-4 px-4 flex items-center space-x-2 h-[100px] w-[100px]">
                              {hasPermission("Users", "edit") && (
                                <motion.button
                                  className="text-red-700"
                                  onClick={() => handleEditClick(user)}
                                  aria-label={`Modifica utente ${user?.name}`}
                                  {...buttonAnimations}
                                >
                                  <TbEdit size={20} />
                                </motion.button>
                              )}
                              {hasPermission("Users", "remove") &&
                                (!loginUserId || String(user?.id) !== String(loginUserId)) &&
                                !isMasterAdmin(user?.email) && (
                                <motion.button
                                  className="text-red-700 cursor-pointer"
                                  onClick={() => {
                                    setUserToDelete(user);
                                    setDeleteModalOpen(true);
                                  }}
                                  aria-label={`Elimina utente ${user?.name}`}
                                  {...buttonAnimations}
                                >
                                  <BiSolidTrashAlt size={18} />
                                </motion.button>
                              )}
                            </td>
                          )}
                        </motion.tr>
                      ))
                    ) : (
                      <motion.tr variants={itemVariants}>
                        <td colSpan={(hasPermission("Users", "edit") || hasPermission("Users", "remove")) ? 6 : 5} className="text-center py-4">
                          Nessun utente trovato
                        </td>
                      </motion.tr>
                    )}
                  </tbody>
                )}
              </table>
            </motion.div>
            {users && users?.length > 0 && (
              <PaginationComponent
                page={page}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                setPage={setPage}
                setRowsPerPage={setRowsPerPage}
                className="mt-6"
              />
            )}
          </motion.section>
        </motion.div>
      )}
      </AnimatePresence>
      {typeof document !== "undefined" &&
        createPortal(
          <ConfirmationDialog
            isOpen={deleteModalOpen}
            title="Are you sure you want to delete the user?"
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            confirmText="Yes"
            cancelText="No"
          />,
          document.body
        )}
    </>
  );
});
UserManagement.displayName = "UserManagement";

UserManagement.propTypes = {
  hasPermission: PropTypes.func.isRequired,
};

export default UserManagement;